import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { custApi, apiError } from "@/lib/api";
import { hostBrand } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, PartyPopper, Gift, Lock } from "lucide-react";

const DEFAULT_CONSENT = (store) =>
  `I agree to receive marketing and promotional emails from ${store} (via ${hostBrand().name}). I can unsubscribe at any time.`;

export default function JoinPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const focusProgram = params.get("program") || "";
  const [store, setStore] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [f, setF] = useState({ name: params.get("n") || "", phone: params.get("p") || "", email: params.get("e") || "", password: "" });
  const [usePassword, setUsePassword] = useState(false);
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    custApi.get(`/public/tenant/${slug}`).then(({ data }) => setStore(data)).catch(() => setStore({ name: slug }));
    custApi.get(`/public/tenant/${slug}/loyalty/programs`).then(({ data }) => {
      setPrograms(data || []);
      const pre = new Set();
      (data || []).forEach((p) => { if (p.enrollment === "auto" || p.id === focusProgram) pre.add(p.id); });
      setSelected(pre);
    }).catch(() => setPrograms([]));
  }, [slug, focusProgram]);

  const consentText = DEFAULT_CONSENT(store?.name || slug);
  const focused = programs.find((p) => p.id === focusProgram);

  const toggle = (id, v) => setSelected((s) => { const n = new Set(s); v ? n.add(id) : n.delete(id); return n; });

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (usePassword && f.password && f.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      const program_ids = [...new Set([...programs.filter((p) => p.enrollment === "auto").map((p) => p.id), ...selected])];
      const { data } = await custApi.post(`/public/tenant/${slug}/optin`, {
        name: f.name, phone: f.phone, email: f.email,
        email_opt_in: agree, consent_text: agree ? consentText : "",
        program_ids,
        password: (usePassword && f.password) ? f.password : undefined,
      });
      if (data.token) {
        localStorage.setItem("mc_customer_token", data.token);
        window.location.href = `/portal?store=${slug}`;
        return;
      }
      setDone({ enrolled: data.enrolled_programs || [], matched: data.matched });
    } catch (er) { setErr(apiError(er.response?.data?.detail) || er.message); }
    finally { setLoading(false); }
  };

  const heading = focused ? `Join ${focused.title}` : "Join & earn rewards";

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center px-4 py-10">
      <div className="max-w-sm w-full border border-border p-8 bg-card">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{store?.name || slug}</div>
        <h1 className="font-display font-extrabold tracking-tighter text-2xl mt-1">{done ? "You're in!" : heading}</h1>

        {focused && !done && (
          <div className="mt-4 border-l-4 pl-3 py-1" style={{ borderColor: focused.accent || "#0033A0" }} data-testid="join-program-banner">
            <div className="flex items-center gap-2 font-semibold text-sm"><Gift className="h-4 w-4" style={{ color: focused.accent || "#0033A0" }} /> {focused.reward}</div>
            {focused.goal ? <div className="text-xs text-muted-foreground mt-0.5">Collect {focused.goal} {focused.earn_type === "stamps" ? "stamps" : "points"} to earn it.</div> : null}
          </div>
        )}

        {done ? (
          <div className="mt-6" data-testid="join-success">
            <PartyPopper className="h-9 w-9 text-primary" />
            <p className="text-sm text-muted-foreground mt-3">
              We've emailed a one-tap sign-in link to <b className="text-foreground">{f.email}</b>. Open it to access your rewards and track your orders — no password needed.
            </p>
            {done.enrolled?.length > 0 && (
              <div className="mt-4 border border-border p-3" data-testid="join-enrolled-list">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">You're enrolled in</div>
                {done.enrolled.map((t, i) => <div key={i} className="text-sm font-medium flex items-center gap-1.5"><Gift className="h-3.5 w-3.5 text-primary" /> {t}</div>)}
              </div>
            )}
            <Link to={`/portal?store=${slug}`} className="block text-sm text-primary underline mt-4" data-testid="join-goto-portal">Go to your account</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="join-form">
            <div><Label>Name</Label><Input className="rounded-none mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="join-name-input" /></div>
            <div><Label>Phone <span className="text-muted-foreground normal-case">(your account number)</span></Label><Input type="tel" className="rounded-none mt-1" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="(415) 555-2671" required data-testid="join-phone-input" /></div>
            <div><Label>Email</Label><Input type="email" className="rounded-none mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="join-email-input" /></div>

            {programs.length > 0 && (
              <div className="border border-border p-3" data-testid="join-programs">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Rewards available to you</div>
                {programs.map((p) => {
                  const auto = p.enrollment === "auto";
                  const checked = auto || selected.has(p.id);
                  return (
                    <label key={p.id} className={`flex items-start gap-2 py-1.5 ${auto ? "" : "cursor-pointer"}`} data-testid={`join-program-${p.id}`}>
                      <Checkbox checked={checked} disabled={auto} onCheckedChange={(v) => toggle(p.id, v)} className="mt-0.5" data-testid={`join-program-check-${p.id}`} />
                      <span className="text-sm leading-tight">
                        <span className="font-semibold">{p.title}</span>
                        {auto && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground border border-border px-1 py-0.5">Automatic</span>}
                        <span className="block text-xs text-muted-foreground">{p.reward}{p.goal ? ` · collect ${p.goal} ${p.earn_type === "stamps" ? "stamps" : "points"}` : ""}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            <div>
              {!usePassword ? (
                <button type="button" className="text-xs text-primary underline inline-flex items-center gap-1" onClick={() => setUsePassword(true)} data-testid="join-add-password">
                  <Lock className="h-3 w-3" /> Set a password (optional)
                </button>
              ) : (
                <div>
                  <Label>Password <span className="text-muted-foreground normal-case">(optional)</span></Label>
                  <Input type="password" className="rounded-none mt-1" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="At least 8 characters" data-testid="join-password-input" />
                  <p className="text-[11px] text-muted-foreground mt-1">Add one if you like — otherwise we'll email you a one-tap sign-in link.</p>
                </div>
              )}
            </div>

            <label className="flex items-start gap-2 pt-1 cursor-pointer">
              <Checkbox checked={agree} onCheckedChange={setAgree} data-testid="join-consent-checkbox" className="mt-0.5" />
              <span className="text-xs text-muted-foreground leading-snug">{consentText} <span className="text-muted-foreground/70">(optional)</span></span>
            </label>

            {err && <p className="text-sm text-accent" data-testid="join-error">{err}</p>}
            <Button type="submit" disabled={loading} className="w-full rounded-none" data-testid="join-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join now"}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Already have an account? <Link to={`/portal?store=${slug}`} className="text-primary underline" data-testid="join-signin-link">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
